import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Card, Paper, Divider, Box, Chip } from "@mui/material";
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import UmleitungenPager from './UmleitungenPager'; // Adjust the path if needed
import AdsterraAdUm from "../Ads/AdsterraAdUm";

const LINES = ["Alle", "50", "53", "X53", "54", "55", "58", "X58", "59", "89", "N11"];

export const Umleitungen = () => {
    const [docs, setDocs] = useState([]);
    const [selectedLine, setSelectedLine] = useState(localStorage.getItem("selectedLine") || "Alle");
    const [loading, setLoading] = useState(true); // <-- add loading state
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            const querySnapshot = await getDocs(collection(db, "Umleitungen"));
            const docsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Sort by dateStart (assumes format "dd.MM.yyyy ...")
            docsData.sort((a, b) => {
                const [dayA, monthA, yearA] = a.dateStart.split(" ")[0].split(".");
                const [dayB, monthB, yearB] = b.dateStart.split(" ")[0].split(".");
                const dateA = new Date(Number(yearA), Number(monthA) - 1, Number(dayA));
                const dateB = new Date(Number(yearB), Number(monthB) - 1, Number(dayB));
                return dateA - dateB;
            });

            setDocs(docsData);
            setLoading(false); // <-- set loading to false after data is fetched
        };
        fetchData();
    }, []);

    const [openModal, setOpenModal] = useState(false);
    const [modalImages, setModalImages] = useState([]);
    const [modalImgId, setModalImgId] = useState(0);

    // Handle line selection
    const handleLineClick = (line) => {
        setSelectedLine(line);
        localStorage.setItem("selectedLine", line);
    };

    // Filter docs by selected line, show == true, dateEnd > today
    const today = new Date();

    const filteredDocs = (selectedLine === "Alle"
        ? docs
        : docs.filter(doc => Array.isArray(doc.line) && doc.line.includes(selectedLine))
    ).filter(doc => {
        if (typeof doc.dateEnd === "string" && doc.dateEnd.toLowerCase().includes("bis auf weiteres")) {
            return doc.show !== false;
        }
        const datePartEnd = doc.dateEnd.split(" ")[0];
        const [dayEnd, monthEnd, yearEnd] = datePartEnd.split(".");
        // Set endDate to the end of the day
        const endDate = new Date(Number(yearEnd), Number(monthEnd) - 1, Number(dayEnd || 28), 23, 59, 59, 999);
        return doc.show !== false && today <= endDate;
    });

    return (
        <Box sx={{ p: 2 }}>
            <h2>Umleitungen ({filteredDocs.length})</h2>
            {loading ? (
                <Box sx={{ textAlign: "center", my: 4, fontSize: "1.3rem" }}>
                    Loading...
                </Box>
            ) : (
                <>
                    {/* Line Filter as Clickable List */}
                    <Box sx={{ mb: 3, mx: 1 }}>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            justifyContent: 'flex-start'
                        }}>
                            {LINES.map(line => (
                                <Chip
                                    key={line}
                                    label={line}
                                    onClick={() => handleLineClick(line)}
                                    variant={selectedLine === line ? "filled" : "outlined"}
                                    color={selectedLine === line ? "primary" : "default"}
                                    sx={{
                                        fontSize: "1.2rem",
                                        fontWeight: "bold",
                                        padding: "8px 12px",
                                        height: "40px",
                                        cursor: "pointer",
                                        border: "2px solid",
                                        borderColor: selectedLine === line ? "primary.main" : "#ccc",
                                        '&:hover': {
                                            backgroundColor: selectedLine === line ? "primary.dark" : "action.hover",
                                            borderColor: "primary.main"
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    {/* No Docs Message */}
                    {!loading && filteredDocs.length === 0 && (
                        <Box sx={{ textAlign: "center", my: 4, fontSize: "1.2rem", color: "gray" }}>
                            0 Umleitungen für die Linie {selectedLine}
                        </Box>
                    )}

                    {/* Responsive Cards Layout */}
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        '& > div': {
                            // Mobile: full width
                            width: '100%',
                            // Desktop: much smaller width to ensure 3 columns
                            '@media (min-width: 900px)': {
                                width: 'calc(32% - 10px)',
                                maxWidth: '320px',
                                flexGrow: 0,
                                flexShrink: 0
                            }
                        }
                    }}>
                        {filteredDocs.map(doc => {
                            const datePart = doc.dateStart.split(" ")[0]; // get only "dd.MM.yyyy"
                            const [day, month, year] = datePart.split(".");
                            const startDate = new Date(Number(year), Number(month) - 1, Number(day));

                            const notYetValid = startDate > today;

                            return (
                                <Card key={doc.id} sx={{
                                    p: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
                                    }
                                }}>
                                    {(() => {
                                        // Calculate date differences
                                        const startY = startDate.getFullYear();
                                        const startM = startDate.getMonth();
                                        const startD = startDate.getDate();
                                        const todayY = today.getFullYear();
                                        const todayM = today.getMonth();
                                        const todayD = today.getDate();

                                        // Helper: compare only date part
                                        const isSameDay = (y1, m1, d1, y2, m2, d2) =>
                                            y1 === y2 && m1 === m2 && d1 === d2;

                                        // If startDate is after today (future)
                                        if (startDate > today) {
                                            // If startDate is exactly one day after today
                                            const tomorrow = new Date(today);
                                            tomorrow.setDate(today.getDate() + 1);
                                            if (isSameDay(startY, startM, startD, tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate())) {
                                                return (
                                                    <>
                                                        <p style={{ color: "red", fontWeight: "bold", margin: "0 0 8px 0" }}>
                                                            Noch nicht gültig!
                                                        </p>
                                                        <p style={{ color: "green", fontWeight: "bold", margin: "0 0 8px 0" }}>
                                                            gilt ab morgen
                                                        </p>
                                                    </>
                                                );
                                            }
                                            // Any other future day
                                            return (
                                                <p style={{ color: "red", fontWeight: "bold", margin: "0 0 8px 0" }}>
                                                    Noch nicht gültig!
                                                </p>
                                            );
                                        }
                                        // If startDate is today
                                        if (isSameDay(startY, startM, startD, todayY, todayM, todayD)) {
                                            return (
                                                <p style={{ color: "green", fontWeight: "bold", margin: "0 0 8px 0" }}>
                                                    gilt ab heute
                                                </p>
                                            );
                                        }
                                        // Otherwise, nothing
                                        return null;
                                    })()}
                                    <p style={{ color: "lightGray", margin: "0 0 8px 0" }}>Nr: {doc.number}</p>
                                    <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1rem" }}>
                                        {doc.dateStart} - {doc.dateEnd}
                                    </h3>

                                    <Paper elevation={0} style={{
                                        display: "flex",
                                        paddingBottom: "8px",
                                        margin: "0 0 12px 0"
                                    }}>
                                        <p style={{
                                            fontSize: "1.1rem",
                                            fontWeight: "bold",
                                            margin: 0
                                        }}>
                                            Linie: {doc.line.map(line => (
                                                <span key={line}
                                                    style={{
                                                        fontSize: "1.3rem",
                                                        fontWeight: "bold",
                                                    }}>
                                                    [{line}] </span>
                                            ))}
                                        </p>
                                    </Paper>

                                    <Paper elevation={0}
                                        style={{
                                            textAlign: "center",
                                            fontWeight: "bold",
                                            fontSize: "1.3rem",
                                            padding: "8px 0",
                                            margin: "0 0 12px 0",
                                            backgroundColor: "#f5f5f5",
                                            borderRadius: "4px"
                                        }}>
                                        {doc.title}
                                    </Paper>

                                    <Box sx={{ flexGrow: 1, mb: 2 }}>
                                        <p style={{ margin: 0, lineHeight: 1.4 }}>{doc.description}</p>
                                    </Box>

                                    {/* Display images if available */}
                                    {Array.isArray(doc.image) && doc.image.length > 0 && (
                                        <Paper sx={{ p: 1, backgroundColor: "#fafafa" }}>
                                            <Box sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                justifyContent: 'center'
                                            }}>
                                                {doc.image.map((img, idx) => (
                                                    <Box
                                                        key={idx}
                                                        component="img"
                                                        src={img}
                                                        alt={`Umleitung ${doc.number} Bild ${idx + 1}`}
                                                        sx={{
                                                            width: 60,
                                                            height: 60,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            cursor: 'pointer',
                                                            border: '2px solid transparent',
                                                            '&:hover': {
                                                                border: '2px solid primary.main',
                                                                transform: 'scale(1.05)'
                                                            },
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        onClick={() => {
                                                            setModalImages(doc.image);
                                                            setModalImgId(idx);
                                                            setOpenModal(true);
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Paper>
                                    )}
                                </Card>
                            )
                        })}
                    </Box>
                </>
            )}
            <Divider style={{ margin: "20px 0" }} />
            {/* <Paper elevation={2} sx={{ p: 2, textAlign: "center", mt: 3 }}>
                <p style={{ fontSize: "1rem", margin: 0 }}>
                    <a href="https://www.nahverkehr-ffm.de/verkehrshinweise" target="_blank" rel="noopener noreferrer">
                        ÖPNV Verkehrshinweise Frankfurt Online
                    </a> 
                </p>
            </Paper> */}

            {/* <Card>
                <AdsterraAdUm />
            </Card> */}


            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
                <UmleitungenPager images={modalImages} imgId={modalImgId} onClose={() => setOpenModal(false)} />
            </Dialog>
        </Box>
    );
};
